/**
 * Traveler chips (M / S) for schedule rows.
 * Solo stops never show chips (travelers.length < 2).
 * On shared stops only: drop a chip when title or detail already
 * names that traveler (Mike, Michael, Suzy, Suzanne, or word-boundary
 * initials). Chip row sits under notes — time column and vote thumbs
 * stay clean.
 */
import type { Traveler } from "../types";

const MICHAEL_NAMES = /\b(mike|michael)\b/i;
const SUZY_NAMES = /\b(suzy|suzanne)\b/i;
/** Word-boundary initials — matches "S outbound", not "SFO" / "SCL". */
const INITIAL_M = /(^|[^A-Za-z])M([^A-Za-z]|$)/;
const INITIAL_S = /(^|[^A-Za-z])S([^A-Za-z]|$)/;

export function travelerNamedInText(
  travelerId: string,
  text: string,
): boolean {
  if (!text.trim()) return false;
  if (travelerId === "michael") {
    return MICHAEL_NAMES.test(text) || INITIAL_M.test(text);
  }
  if (travelerId === "suzy") {
    return SUZY_NAMES.test(text) || INITIAL_S.test(text);
  }
  return false;
}

/**
 * Travelers who should show an M/S chip for this schedule item.
 * No travelers field → no chips. Solo assignment → no chips.
 * Shared only: strip anyone already named in title/detail.
 */
export function chipsForScheduleItem(
  item: {
    title: string;
    detail?: string;
    travelers?: string[];
  },
  travelers: Traveler[],
): Traveler[] {
  if (!item.travelers || item.travelers.length < 2) return [];

  const text = `${item.title ?? ""} ${item.detail ?? ""}`;
  const assigned = travelers.filter((t) => item.travelers!.includes(t.id));
  if (assigned.length < 2) return [];

  return assigned.filter((t) => !travelerNamedInText(t.id, text));
}
